import {
  type HierarchicalCategoryDTO,
  type IngredientDTO,
  type MachineFeatureDTO
} from '@/services/generated/ECAPIProductsDTOs.ts';
import decodeCategories from '@/services/utils/decodeCategories.ts';
import {
  getAromaticsIcon,
  getCupSizeDataFromCategoryCode,
  getRecipesIcon
} from '@/widgets/utils/FeatureIconsLookup.ts';
import type {
  FeatureSpecification,
  PropertySpecification,
  Specification,
  Specifications
} from '../Specification.ts';
import { findCategoryByCode, findCategoryById } from '../Category.ts';
import { type I18NInstance } from '@/utils/createI18nInstance.ts';
import { type ProductTranslations } from '../config/index.ts';

/**
 * Extract specific information from the product DTO, which we are calling a Product Specification
 * Information such as coffee intensity, aromatics, cup sizing, machine weight etc.
 */
export function fromEcapiDto(
  productDto: any,
  categories: HierarchicalCategoryDTO[],
  productI18nInstance: I18NInstance<ProductTranslations>
): Specifications {
  const { t } = productI18nInstance;

  const features = Object.fromEntries(
    (productDto?.features ?? []).map(
      (feature: MachineFeatureDTO, index: number) => [
        feature.featureCode ?? `__${feature.name}`,
        {
          title: feature.name?.trim(),
          order: index,
          description: feature.description,
          unitOfMeasure: feature.unitOfMeasure,
          icon: feature.featureCode
        } as FeatureSpecification
      ]
    )
  );

  const ingredients = (productDto?.ingredients ?? []).map(
    (ingredient: IngredientDTO, index: number): FeatureSpecification => ({
      title: ingredient.type?.toLowerCase(),
      description: ingredient.foodFact
        ? `${ingredient.text} ${ingredient.foodFact}`
        : ingredient.text,
      order: index
    })
  );

  // Note: intensity=0 is not a valid intensity number
  const isValidCapsuleProperty = ([key, value]: [string, unknown]) =>
    key !== 'intensity' || value !== 0;
  const capsuleProperties = Object.entries(productDto.capsuleProperties ?? {});

  const properties = Object.fromEntries(
    capsuleProperties.filter(isValidCapsuleProperty).map(([key, value]) => [
      key,
      {
        title: t(`product.specification.${key}`) ?? '',
        value
      } as PropertySpecification
    ])
  );
  const superCategories = new Set(decodeCategories(productDto.supercategories));

  return {
    ingredients,
    ...features,
    ...properties,
    ...getDecafSpecification(
      productDto.decaffeinated,
      categories,
      superCategories,
      productI18nInstance
    ),
    ...getCupSizeSpecification(categories, superCategories),
    ...getRecipeSpecification(categories, superCategories),
    ...getAromaticProfileSpecification(categories, superCategories),
    ...getCollectionSpecification(categories, superCategories)
  };
}

function getDecafSpecification(
  hasDecaffeinatedHMCFlag: boolean,
  categories: HierarchicalCategoryDTO[],
  superCategories: Set<string>,
  productI18nInstance: I18NInstance<ProductTranslations>
): Specifications {
  const decafIcon = '24/cupsize/cupsize-ristretto-ol';
  const { t } = productI18nInstance;

  // Note: For now there are many decaffeinated coffees that do not have the new property set,
  // so we must maintain both behaviors for now.
  if (hasDecaffeinatedHMCFlag) {
    return {
      decaf: {
        value: true,
        icon: decafIcon,
        title: t('product.specification.decaffeinated')
      }
    };
  }

  const decafCategory = findCategoryByCode(categories, 'capsulesDecaffeinato');
  if (decafCategory && superCategories.has(decafCategory.id)) {
    return {
      decaf: {
        value: true,
        icon: decafIcon,
        title: decafCategory.name?.trim()
      }
    };
  }

  return {
    decaf: {
      value: false,
      icon: decafIcon,
      title: t('product.specification.decaffeinated')
    }
  };
}

function getCupSizeSpecification(
  categories: HierarchicalCategoryDTO[],
  superCategories: Set<string>
): Specifications {
  const cupSizeCategories =
    findCategoryByCode(categories, 'capsule-cupSize')?.subCategories || [];
  const matchingCupSizeCategories = cupSizeCategories.filter((cat) =>
    superCategories.has(cat)
  );

  const cupSizeSpecifications: Specification[] =
    matchingCupSizeCategories.reduce((acc, currentCategory) => {
      const category = findCategoryById(categories, currentCategory);
      const staticData = getCupSizeDataFromCategoryCode(category?.code);

      if (staticData) {
        acc.push({
          title: category?.name?.trim(),
          value: category?.code,
          measure: staticData.mlCoffee, // TODO: should come from ECAPI
          icon: `24/${staticData.type}/${staticData.dsId}` // TODO: use actual icon data from ECAPI?
        } as PropertySpecification);
      }

      return acc;
    }, [] as PropertySpecification[]);

  if (cupSizeSpecifications.length !== 0) {
    return { cupSizes: cupSizeSpecifications };
  }

  return {};
}

function getCollectionSpecification(
  categories: HierarchicalCategoryDTO[],
  superCategories: Set<string>
): Specifications {
  const collectionCategories = [
    ...(findCategoryByCode(categories, 'capsule-range')?.subCategories || []),
    ...(findCategoryByCode(categories, 'accessory-collection')?.subCategories ||
      []),
    ...(findCategoryByCode(categories, 'machine-range')?.subCategories || [])
  ].flat();

  const matchingCollectionCategories = collectionCategories.filter((cat) =>
    superCategories.has(cat)
  );

  const collectionSpecifications: Specification[] =
    matchingCollectionCategories.reduce((acc, cat) => {
      const category = findCategoryById(categories, cat);

      acc.push({
        value: category?.code,
        title: category?.name?.trim()
      } as PropertySpecification);

      return acc;
    }, [] as PropertySpecification[]);

  if (collectionSpecifications.length !== 0) {
    return { collections: collectionSpecifications };
  }

  return {};
}

function getAromaticProfileSpecification(
  categories: HierarchicalCategoryDTO[],
  superCategories: Set<string>
): Specifications {
  const aromaticCategories =
    findCategoryByCode(categories, 'capsule-aromatic')?.subCategories || [];
  const matchingAromaticCategories = aromaticCategories.filter((cat) =>
    superCategories.has(cat)
  );

  const aromaticSpecifications: Specification[] =
    matchingAromaticCategories.reduce((acc, cat) => {
      const category = findCategoryById(categories, cat);
      const staticData = category ? getAromaticsIcon(category.code) : '';

      if (staticData) {
        acc.push({
          value: category?.code,
          title: category?.name?.trim(),
          icon: staticData
        } as PropertySpecification);
      }

      return acc;
    }, [] as PropertySpecification[]);

  if (aromaticSpecifications.length !== 0) {
    return { aromatics: aromaticSpecifications };
  }

  return {};
}

function getRecipeSpecification(
  categories: HierarchicalCategoryDTO[],
  superCategories: Set<string>
): Specifications {
  // since there are recipe categories in the "capsule-cupSize-" and in "recipe-"" category, we need to combine them
  const combinedRecipeCategories = [
    ...(findCategoryByCode(categories, 'capsule-cupSize')?.subCategories || []),
    ...(findCategoryByCode(categories, 'recipe')?.subCategories || [])
  ];

  const matchingRecipeCategories = combinedRecipeCategories.filter((cat) =>
    superCategories.has(cat)
  );

  const recipeSpecifications: Specification[] = matchingRecipeCategories.reduce(
    (acc, cat) => {
      const category = findCategoryById(categories, cat);
      const staticData = category ? getRecipesIcon(category.code) : '';

      if (staticData) {
        acc.push({
          value: category?.code,
          title: category?.name?.trim(),
          icon: staticData
        } as PropertySpecification);
      }

      return acc;
    },
    [] as PropertySpecification[]
  );

  if (recipeSpecifications.length !== 0) {
    return { recipes: recipeSpecifications };
  }

  return {};
}
